import { useState, useEffect } from 'react';

import { appColors } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { EnterButton } from '@backrs_ui/grow-ui/src/components/Lubav/EnterButton';
import { FileUpload } from '@backrs_ui/grow-ui/src/components/Lubav/FileUpload';
import { ResourceNugget } from '@backrs_ui/grow-ui/src/components/Lubav/nuggets/ResourceNugget';
import { axiosInstance as axios } from '@backrs_ui/grow-ui/src/core/axios';
import {
  Contact,
  MessageMode,
  SearchedUser,
  SenderType,
  SolutionSessionMessage,
} from '@backrs_ui/grow-ui/src/models/models';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  getSessionState,
  getActiveLubavSession,
  createSessionAction,
} from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState.utils';
import {
  setUserInput,
  setUserMessage,
  setUserSearchResults,
} from '@backrs_ui/grow-ui/src/redux/solutionSession/solutionSession';

import {
  SxProps,
  Theme,
  Box,
  Button,
  TextField,
  FormControl,
  Typography,
  InputAdornment,
  Slider,
  Tooltip,
} from '@mui/material';

export type Message = Readonly<{
  text: string;
  sender: SenderType;
  mode?: MessageMode;
  id?: string | null;
  question?: string;
  type?: string;
  options?: string[];
  include_freeform?: boolean;
  required?: boolean;
  user_selection_options?: Contact[];
  placeholder?: string;
  ask_to_regenerate?: boolean;
}>;

type RenderQuestionProps = Readonly<{
  message: SolutionSessionMessage;
  sendQAMessage: (message: string, LLMInput?: string | string[]) => void;
  required: boolean;
  setFreeformIsActive: (isActive: boolean) => void;
  setFreeformIsNumeric: (isNumeric: boolean) => void;
  editingMessageId: string | null;
  userId: number;
  roles?: number[];
}>;

type SearchParams = Readonly<{
  query: string;
  userId: number;
  roles?: number[];
}>;

const { fileUpload } = appStrings.modules.chatBox;

const TYPES_WITH_SUBMIT = ['multi_select', 'multi_freeform', 'multi_numeric', 'slider'] as const;
const TYPES_WITHOUT_SKIP = ['file_upload'] as const;

export const RenderQuestion = ({
  message,
  sendQAMessage,
  required,
  setFreeformIsActive,
  setFreeformIsNumeric,
  editingMessageId,
  userId,
  roles,
}: RenderQuestionProps) => {
  const dispatch = useAppDispatch();
  const activeSession = useAppSelector(getActiveLubavSession);

  const { userMessage, chatMode, userInput, userSearchResults } = useAppSelector(
    (state) => getSessionState(state, activeSession).solutionSession,
  );

  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [visibleOptions, setVisibleOptions] = useState<number[]>([]);
  const [sliderValue, setSliderValue] = useState(1);

  // check if input box should be active / numeric
  const isFreeformActive = editingMessageId
    ? false
    : message.include_freeform || ['numeric', 'freeform'].includes(message.type || '');
  const isNumeric = message.type === 'numeric';

  setFreeformIsActive(isFreeformActive);
  setFreeformIsNumeric(isNumeric);

  // show options one by one
  useEffect(() => {
    if (message.options) {
      const showOptions = async () => {
        setVisibleOptions([]);
        for (let i = 0; i < (message?.options?.length || 0); i++) {
          await new Promise((resolve) => setTimeout(resolve, 10));

          setVisibleOptions((prev) => [...prev, i]);
        }
      };

      showOptions();
    }
  }, [message.options]);

  if (!activeSession) {
    return null;
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();

      if (userInput?.type === 'SINGLE_INPUT' && !userInput.value.trim() && required) {
        return;
      }

      if (
        userInput?.type === 'MULTIPLE_INPUT_MAP' &&
        Object.keys(userInput.value).length === 0 &&
        required
      ) {
        return;
      }

      if (userInput?.type === 'MULTIPLE_INPUT' && userInput.value.length !== 0) {
        sendQAMessage(userInput.value.join(', '), userMessage);
      }

      if (userInput?.type === 'MULTIPLE_INPUT_MAP') {
        const formattedObject = Object.entries(userInput.value)
          .map(([key, value]) => `${key}: ${value}`)
          .join(', ');

        sendQAMessage(formattedObject, userMessage);
      }

      if (userInput?.type === 'SINGLE_INPUT') {
        sendQAMessage(userInput?.value, userMessage);
      }

      resetInputs();
    }
  };

  const resetInputs = () => {
    setSelectedOptions([]);
    dispatch(createSessionAction(setUserMessage, activeSession)(''));
    dispatch(createSessionAction(setUserInput, activeSession)(undefined));
  };

  const handleInputChange = (value: string) => {
    dispatch(createSessionAction(setUserMessage, activeSession)(value));
    dispatch(createSessionAction(setUserInput, activeSession)({ type: 'SINGLE_INPUT', value }));
  };

  const renderSubmit = () => {
    const isDisabled =
      (message.type === 'multi_select' && selectedOptions.length === 0) ||
      (userInput?.type === 'SINGLE_INPUT' && !userInput.value.trim() && required) ||
      (userInput?.type === 'MULTIPLE_INPUT_MAP' &&
        Object.keys(userInput.value).length === 0 &&
        required);

    return (
      <EnterButton
        onClick={() => {
          if (userInput?.type === 'SINGLE_INPUT' && !userInput.value.trim() && required) {
            return;
          }
          if (
            userInput?.type === 'MULTIPLE_INPUT_MAP' &&
            Object.keys(userInput.value).length === 0 &&
            required
          ) {
            return;
          }

          if (userInput?.type === 'MULTIPLE_INPUT' && userInput.value.length > 0) {
            sendQAMessage(userInput.value.join(', '), userMessage);
          }

          if (userInput?.type === 'MULTIPLE_INPUT_MAP') {
            const formattedObject = Object.entries(userInput.value)
              .map(([key, value]) => `${key}: ${value}`)
              .join(', ');

            sendQAMessage(formattedObject, userMessage);
          }

          if (userInput?.type === 'SINGLE_INPUT') {
            sendQAMessage(userInput.value, userMessage);
          }

          setSelectedOptions([]);
          dispatch(createSessionAction(setUserInput, activeSession)(undefined));
        }}
        label="Submit"
        showEnterHint={true}
        disabled={isDisabled}
      />
    );
  };

  const renderSkip = (isRequired: boolean) => {
    return (
      <Box sx={skipButtonContainerStyle}>
        {isRequired ? (
          <Tooltip
            title={'This question is required. Please provide your best answer!'}
            placement="bottom-end"
            componentsProps={{
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              tooltip: { sx: tooltipStyle as any },
            }}
          >
            <span>
              <Button
                onClick={() => sendQAMessage('', 'Skip this question.')}
                sx={getSkipButtonStyle(isRequired)}
              >
                <Typography sx={getSkipTypographyStyle(isRequired)}>
                  <strong>Skip</strong>
                </Typography>
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Button
            onClick={() => sendQAMessage('', 'Skip this question.')}
            sx={getSkipButtonStyle(isRequired)}
          >
            <Typography sx={getSkipTypographyStyle(isRequired)}>
              <strong>Skip</strong>
            </Typography>
          </Button>
        )}
      </Box>
    );
  };

  const RenderUserSearch = ({ userId, roles }: { userId: number; roles?: number[] }) => {
    const [searchTimer, setSearchTimer] = useState<NodeJS.Timeout | null>(null);

    const handleSearch = async ({ query, userId, roles }: SearchParams) => {
      if (!query.trim()) {
        dispatch(createSessionAction(setUserSearchResults, activeSession)([]));

        return;
      }

      if (searchTimer) {
        clearTimeout(searchTimer);
      }

      const newTimer = setTimeout(async () => {
        try {
          const response = await axios.get(`/api/search-for-user`, {
            params: { query, full_last_name: false, user_id: userId, roles },
          });

          dispatch(createSessionAction(setUserSearchResults, activeSession)(response.data));
        } catch (err) {
          console.error('Error searching for users:', err);
        }
      }, 300);

      setSearchTimer(newTimer);
    };

    const handleProfileClick = async (user: SearchedUser) => {
      const profile = await axios
        .get(`/api/get-user-profile`, {
          params: { user_id: user.id, as_superuser: false },
        })
        .then((res) => res.data);

      sendQAMessage(`ID: ${user.id}\n\nProfile: ${profile}`, user.name);
    };

    return (
      <Box>
        <TextField
          label="Search Users"
          variant="outlined"
          fullWidth
          value={userInput?.value}
          onChange={(e) => {
            dispatch(
              createSessionAction(
                setUserInput,
                activeSession,
              )({ type: 'SINGLE_INPUT', value: e.target.value }),
            );
            handleSearch({
              query: e.target.value,
              userId,
              roles,
            });
          }}
          placeholder="Type to search for users by name"
          sx={{ margin: '16px 0px' }}
        />

        <Box sx={searchResultsContainerStyle}>
          {userSearchResults.length > 0
            ? userSearchResults.map((user) => (
                <Box
                  key={user.id}
                  onClick={() => {
                    handleProfileClick(user);
                  }}
                  sx={searchResultStyle}
                >
                  <Typography>{user.name}</Typography>
                </Box>
              ))
            : userInput && <Typography>No users found</Typography>}
        </Box>
      </Box>
    );
  };

  const renderSingleSelect = () => (
    <FormControl fullWidth sx={{ mb: 1 }}>
      <Box sx={optionsContainerStyle}>
        {message?.options?.map((option, index) => (
          <Box
            key={index}
            onClick={() => {
              sendQAMessage(option, option);
              resetInputs();
            }}
            sx={getOptionBoxStyle(userInput?.value === option, visibleOptions.includes(index))}
          >
            {option}
          </Box>
        ))}
      </Box>
    </FormControl>
  );

  const renderUserSelect = () => {
    const handleNameClick = async (userId: string, name: string) => {
      try {
        const profile = await axios
          .get(`/api/get-user-profile`, {
            params: { user_id: userId, as_superuser: false },
          })
          .then((res) => res.data);

        dispatch(createSessionAction(setUserMessage, activeSession)(name));
        dispatch(
          createSessionAction(setUserInput, activeSession)({ type: 'SINGLE_INPUT', value: userId }),
        );
        sendQAMessage(`ID: ${userId}\n\nProfile: ${profile}`, name);
        resetInputs();
      } catch (err) {
        console.error('Error fetching user profile:', err);
      }
    };

    return (
      <Box sx={userSelectContainerSx}>
        {message?.user_selection_options?.map((contact) => (
          <Box
            key={contact.id}
            onClick={() => handleNameClick(contact.id, contact.metadata.name)}
            sx={userSelectStyle}
          >
            <Box sx={{ width: '100%' }}>
              <ResourceNugget
                key={contact.id}
                type={contact.metadata.role === 'professional' ? 'backrs' : 'students'}
                resource={contact}
                disableExpansion={true}
              />
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  const renderMultiSelect = () => {
    const handleBoxClick = (option: string) => {
      const updatedOptions = selectedOptions.includes(option)
        ? selectedOptions.filter((item) => item !== option)
        : [...selectedOptions, option];

      setSelectedOptions(updatedOptions);
      const optionsString = updatedOptions.join(', ');

      handleInputChange(optionsString);
      dispatch(
        createSessionAction(
          setUserInput,
          activeSession,
        )({ type: 'MULTIPLE_INPUT', value: updatedOptions }),
      );
    };

    return (
      <FormControl fullWidth sx={{ mb: 1 }}>
        <Box sx={optionsContainerStyle} onKeyPress={handleKeyPress} tabIndex={0}>
          {message?.options?.map((option, index) => (
            <Box
              key={index}
              onClick={() => handleBoxClick(option)}
              sx={getOptionBoxStyle(
                selectedOptions.includes(option),
                visibleOptions.includes(index),
              )}
            >
              {option}
            </Box>
          ))}
        </Box>
      </FormControl>
    );
  };

  const renderFileUpload = () => {
    return (
      <FileUpload
        userId={userId}
        addToProfile={true}
        onSubmit={(fileName, content) => {
          if (!content) throw new Error('No content found');

          dispatch(
            createSessionAction(
              setUserMessage,
              activeSession,
            )(fileUpload.fileNameDisplay(fileName)),
          );
          dispatch(
            createSessionAction(
              setUserInput,
              activeSession,
            )({
              type: 'SINGLE_INPUT',
              value: fileUpload.fileContentForLLM(fileName),
            }),
          );
          sendQAMessage(
            fileUpload.fileContentForLLM(fileName),
            fileUpload.fileNameDisplay(fileName),
          );
        }}
        onError={() => {
          alert(fileUpload.uploadError);
        }}
        cancelComponent={renderSkip(required)}
      />
    );
  };

  const renderMultiFreeform = (numericOnly = false) => {
    if (userInput?.type !== 'MULTIPLE_INPUT_MAP') {
      dispatch(
        createSessionAction(setUserInput, activeSession)({ type: 'MULTIPLE_INPUT_MAP', value: {} }),
      );

      return;
    }

    return (
      <Box sx={{ mb: 2 }}>
        {message.options?.map((option) => (
          <Box key={option} sx={multiFreeformContainerStyle}>
            <TextField
              fullWidth
              value={userInput.value[option] || ''}
              onKeyPress={(e) => handleKeyPress(e)}
              onChange={(e) => {
                const newValue = e.target.value;

                if (numericOnly) {
                  const numericValue = newValue.replace(/[^0-9]/g, '');

                  e.target.value = numericValue;
                }

                const newInput = { ...userInput.value, [option]: newValue };

                dispatch(
                  createSessionAction(
                    setUserInput,
                    activeSession,
                  )({ type: 'MULTIPLE_INPUT_MAP', value: newInput }),
                );

                dispatch(
                  createSessionAction(
                    setUserMessage,
                    activeSession,
                  )(
                    Object.entries(newInput)
                      .map(([key, value]) => `${key}: ${value}`)
                      .join('\n\n'),
                  ),
                );
              }}
              InputProps={{
                startAdornment: <InputAdornment position="start">{option}:</InputAdornment>,
                sx: textFieldInputStyle,
                ...(numericOnly && {
                  inputProps: { inputMode: 'numeric', pattern: '[0-9]*' },
                }),
              }}
              sx={freeformTextFieldStyle}
            />
          </Box>
        ))}
      </Box>
    );
  };

  const renderSlider = () => {
    const marks = message.options?.map((option, index) => ({
      value: Number(index) + 1,
      label: option,
    }));

    // get indices for the middle labels
    const middleIndices = Array.from(
      { length: (message?.options?.length || 0) - 2 },
      (_, i) => i + 1,
    );
    const middleSelectorsString = middleIndices
      .map((index) => `& .MuiSlider-markLabel[data-index="${index}"]`)
      .join(', ');

    return (
      <Box sx={sliderContainerStyle}>
        <Slider
          value={sliderValue}
          onChange={(_, value) => {
            setSliderValue(Number(value));
            const selectedOption = message.options?.[Number(value) - 1];

            if (selectedOption) {
              handleInputChange(selectedOption);
            }
          }}
          onKeyPress={handleKeyPress}
          min={1}
          max={Number(message.options?.length)}
          step={1}
          marks={marks}
          sx={{
            ...sliderStyle,
            [`& .MuiSlider-markLabel[data-index="${sliderValue - 1}"]`]: {
              color: appColors.color.blue1,
              fontWeight: '600',
            },
            [middleSelectorsString]: {
              display: { xs: 'none', sm: 'none', md: 'none', lg: 'block' },
            },
          }}
        />
      </Box>
    );
  };

  const renderQuestionByType = ({ userId, roles }: { userId: number; roles?: number[] }) => {
    switch (message.type) {
      case 'freeform':
        return;
      case 'single_select':
        return renderSingleSelect();
      case 'multi_select':
        return renderMultiSelect();
      case 'file_upload':
        return renderFileUpload();
      case 'numeric':
        return;
      case 'multi_freeform':
        return renderMultiFreeform();
      case 'multi_numeric':
        return renderMultiFreeform(true);
      case 'user_select':
        return renderUserSelect();
      case 'user_search':
        return RenderUserSearch({ userId, roles });
      case 'user_suggestion':
        return renderUserSelect();
      case 'slider':
        return renderSlider();
      default:
        return null;
    }
  };

  return (
    <Box>
      <Box sx={{ paddingRight: '40px' }}>{renderQuestionByType({ userId, roles })}</Box>
      <Box sx={buttonContainerStyle}>
        <Box sx={{ flexGrow: 1 }}>
          {TYPES_WITH_SUBMIT.includes(message.type as (typeof TYPES_WITH_SUBMIT)[number]) &&
            renderSubmit()}
        </Box>
        {!TYPES_WITHOUT_SKIP.includes(message.type as (typeof TYPES_WITHOUT_SKIP)[number]) &&
          !chatMode && <Box sx={skipButtonStyle}>{renderSkip(required)}</Box>}
      </Box>
    </Box>
  );
};

const buttonContainerStyle: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: '10px',
} as const;

const freeformTextFieldStyle: SxProps<Theme> = {
  mb: 1,
  backgroundColor: appColors.color.blue4,
  borderRadius: '8px',
  '& .MuiOutlinedInput-root': {
    '& fieldset': { border: 'none' },
  },
  width: '50%',
  padding: '0px',
  height: 'auto',
  lineHeight: '1.2',
  margin: '0px',
} as const;

const optionsContainerStyle: SxProps<Theme> = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 1,
} as const;

const getOptionBoxStyle = (isSelected: boolean, isVisible: boolean): SxProps<Theme> => ({
  padding: '6px 16px',
  borderRadius: '8px',
  border: isSelected ? `2px solid ${appColors.color.blue1}` : `2px solid ${appColors.color.blue4}`,
  backgroundColor: appColors.color.blue4,
  color: appColors.color.blue1,
  cursor: 'pointer',
  userSelect: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '11pt',
  fontWeight: '500',
  opacity: isVisible ? 1 : 0,
  transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
  transition: 'opacity 0.3s ease, transform 0.3s ease',
});

const userSelectStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  width: '100%',
  alignItems: 'center',
  justifyContent: 'flex-start',
  padding: backrs.spacing['spacing-xxs'],
  borderRadius: '14px',
  cursor: 'pointer',
  transition: 'background-color 0.3s ease',
  '&:hover': {
    backgroundColor: backrs.colors['Brand/500'],
  },
  '&:active': {
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
  },
});

const tooltipStyle: SxProps<Theme> = {
  backgroundColor: '#f5f5f5',
  color: '#999',
  fontSize: '10pt',
  borderRadius: '8px',
  padding: '8px 12px',
} as const;

const skipButtonContainerStyle: SxProps<Theme> = {
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
} as const;

const getSkipButtonStyle = (isRequired: boolean): SxProps<Theme> =>
  ({
    borderRadius: '8px',
    minWidth: 'unset',
    width: '32px',
    textTransform: 'none',
    color: isRequired ? '#b0b0b0' : 'initial',
    pointerEvents: isRequired ? 'none' : 'auto',
    '&:hover': {
      backgroundColor: 'transparent',
      '& .MuiTypography-root': {
        color: appColors.neutral.gray4,
      },
    },
  }) as const;

const getSkipTypographyStyle = (isRequired: boolean): SxProps<Theme> =>
  ({
    fontSize: '9pt',
    color: isRequired ? appColors.neutral.gray4 : appColors.neutral.gray1,
  }) as const;

const sliderStyle: SxProps<Theme> = {
  '& .MuiSlider-mark': {
    backgroundColor: appColors.color.blue1,
  },
  '& .MuiSlider-rail': {
    backgroundColor: appColors.color.blue4,
  },
  '& .MuiSlider-track': {
    border: 'transparent',
    backgroundColor: 'transparent',
  },
  '& .MuiSlider-thumb': {
    backgroundColor: appColors.color.blue1,
  },
  '& .MuiSlider-markLabel': {
    fontSize: 'clamp(8pt, 0.8vw, 11pt)',
    color: appColors.neutral.gray1,
  },
} as const;

const sliderContainerStyle: SxProps<Theme> = {
  width: '85%',
  padding: '10px',
  paddingRight: '50px',
  marginRight: '-40px',
} as const;

const multiFreeformContainerStyle: SxProps<Theme> = {
  mb: 2,
  display: 'flex',
  flexDirection: 'row',
  gap: 2,
  alignItems: 'center',
} as const;

const skipButtonStyle: SxProps<Theme> = {
  flexShrink: 0,
  marginRight: '40px',
} as const;

const searchResultStyle: SxProps<Theme> = {
  padding: '8px',
  cursor: 'pointer',
  borderBottom: '1px solid #eee',
} as const;

const searchResultsContainerStyle: SxProps<Theme> = {
  maxHeight: '300px',
  overflowY: 'auto',
} as const;

const textFieldInputStyle: SxProps<Theme> = {
  padding: '0px 16px',
  height: 'auto',
  lineHeight: '1.2',
  margin: '0px',
  '& .MuiInputBase-input': {
    padding: '8px 8px',
    fontSize: '10.5pt',
  },
} as const;

const userSelectContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
});
